// 업무 관련 표시 헬퍼

import { ElMessageBox } from 'element-plus'

function toDateOnly(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

/*
 * 마감일이 지났고 아직 손에 쥔 일이면 overdue.
 * 승인대기는 뺀다 — 담당자는 할 일을 마치고 결재를 기다리는 중이다.
 * 서버 배지/요약의 ACTIONABLE(routes/tasks.js)과 같은 술어라야 카드가 빨갛게
 * 칠해진 업무 수와 '지연' 배지 숫자가 맞는다.
 */
export function isOverdue(task) {
  if (!task || !task.dueDate || task.status === 'done' || task.status === 'pending_approval') return false
  return toDateOnly(task.dueDate) < toDateOnly(new Date())
}

// 마감일 상대 표기 (오늘/내일/D-n/n일 지남/기한 없음)
export function formatDueDate(dueDate) {
  if (!dueDate) return '기한 없음'
  const due = toDateOnly(dueDate)
  const today = toDateOnly(new Date())
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24))
  if (diff === 0) return '오늘'
  if (diff === 1) return '내일'
  if (diff === -1) return '어제'
  if (diff < 0) return `${Math.abs(diff)}일 지남`
  if (diff <= 7) return `D-${diff}`
  return new Date(dueDate).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
}

/*
 * D-day 배지(primitives.css .dday)의 톤. formatDueDate 는 문자열만 돌려주고 색은
 * 알려주지 않는다. 카드와 상세가 같은 계산을 각자 들고 있던 것을 여기로 모았다.
 * 끝난 업무는 마감이 지났어도 회색 — 이미 끝난 일을 빨갛게 칠하면 진짜 지연된 카드가 묻힌다.
 */
export function dueTone(task) {
  if (!task || task.status === 'done' || !task.dueDate) return ''
  if (isOverdue(task)) return 'dday--over'
  const diff = Math.round((toDateOnly(task.dueDate) - toDateOnly(new Date())) / 86400000)
  return diff >= 0 && diff <= 1 ? 'dday--soon' : ''
}

/*
 * 피드 시각. 오늘 것은 시:분, 그 외에는 월-일 시:분 — Mono + tabular-nums(u-num)로
 * 찍히므로 '8월 24일 오전 10:00' 같은 가변폭 문자열은 오히려 흔들린다.
 * replace(' ', 'T'): 서버가 dateStrings 로 '2026-08-24 10:00:00' 을 주는데
 * Safari 의 Date 파서는 이 형식을 Invalid Date 로 돌려준다.
 * 메모(TaskCommentBoard)와 로그(TaskActivityTimeline)가 한 피드에 섞이므로 한 함수를 쓴다.
 */
export function formatFeedTime(val) {
  if (!val) return ''
  const d = new Date(String(val).replace(' ', 'T'))
  if (isNaN(d.getTime())) return String(val)
  const p = (n) => String(n).padStart(2, '0')
  const hm = `${p(d.getHours())}:${p(d.getMinutes())}`
  const now = new Date()
  const sameDay = d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  return sameDay ? hm : `${p(d.getMonth() + 1)}-${p(d.getDate())} ${hm}`
}

/*
 * 승인대기 업무의 상태를 바꾸기 전에 묻는다. 서버(PATCH /status)는 이 이동을
 * 완료 요청 **철회**로, 관리자가 완료로 옮기면 **강제 완료**로 처리한다.
 * 카드 한 번 끌었다고 결재가 조용히 사라지면 안 된다.
 * 예전에는 보드 드래그에만 이 확인이 있어서, 같은 이동을 상태 시트 · 상세의
 * 상태 필 · 수정 시트에서 하면 묻지도 않고 철회됐다.
 * 관리자가 아니면 완료로는 못 옮긴다(서버 403 이 이유를 말한다) — 그때는 묻지 않는다.
 * @param {boolean} isAdmin store.getters.isAdmin
 * @returns {Promise<boolean>} 진행해도 되면 true
 */
export async function confirmStatusChange(task, status, isAdmin) {
  if (!task || task.status !== 'pending_approval' || status === 'pending_approval') return true
  const toDone = status === 'done'
  if (toDone && !isAdmin) return true
  try {
    await ElMessageBox.confirm(
      toDone
        ? '이 업무는 완료 승인을 기다리는 중입니다. 완료로 옮기면 승인 없이 관리자 권한으로 강제 완료됩니다.'
        : '이 업무는 완료 승인을 기다리는 중입니다. 상태를 바꾸면 완료 요청이 철회됩니다.',
      toDone ? '강제 완료' : '완료 요청 철회',
      { confirmButtonText: toDone ? '강제 완료' : '철회하고 변경', cancelButtonText: '취소', type: 'warning' }
    )
    return true
  } catch {
    return false
  }
}

// 활동 로그 한글 라벨 + 점 색상
const ACTIVITY_META = {
  // 완료 승인 (TaskActivity.type 이 STRING 이라 서버 마이그레이션은 필요 없었다)
  completion_requested: { label: '완료 요청', color: 'var(--task-pending-approval)' },
  completion_approved: { label: '완료 승인', color: 'var(--color-success)' },
  completion_rejected: { label: '완료 반려', color: 'var(--color-danger)' },
  completion_canceled: { label: '완료 요청 철회', color: 'var(--color-text-muted)' },
  completion_auto_approved: { label: '자동 완료(승인자 없음)', color: 'var(--color-success)' },
  force_completed: { label: '관리자 강제 완료', color: 'var(--color-warning)' },
  approver_changed: { label: '승인자 변경', color: 'var(--color-warning)' },

  created: { label: '업무 생성', color: 'var(--color-primary)' },
  assigned: { label: '담당자 배정', color: 'var(--color-primary)' },
  claimed: { label: '담당자 가져감', color: 'var(--color-primary)' },
  unassigned: { label: '담당자 해제', color: 'var(--color-text-muted)' },
  reassigned: { label: '담당자 재배정', color: 'var(--color-warning)' },
  status_changed: { label: '상태 변경', color: 'var(--color-info)' },
  supporter_added: { label: '서포터 추가', color: 'var(--color-success)' },
  supporter_removed: { label: '서포터 제거', color: 'var(--color-text-muted)' },
  title_changed: { label: '제목 변경', color: 'var(--color-info)' },
  description_changed: { label: '설명 수정', color: 'var(--color-text-muted)' },
  start_changed: { label: '시작일 변경', color: 'var(--color-info)' },
  due_changed: { label: '마감일 변경', color: 'var(--color-info)' },
  priority_changed: { label: '우선순위 변경', color: 'var(--color-info)' },
  comment_added: { label: '메모 작성', color: 'var(--color-text-muted)' },
  comment_edited: { label: '메모 수정', color: 'var(--color-text-muted)' },
  comment_deleted: { label: '메모 삭제', color: 'var(--color-danger)' },
  attachment_added: { label: '첨부 추가', color: 'var(--color-success)' },
  attachment_removed: { label: '첨부 삭제', color: 'var(--color-danger)' },
  updated: { label: '내용 수정', color: 'var(--color-text-muted)' }
}

export function activityLabel(type) { return (ACTIVITY_META[type] || { label: type }).label }
export function activityColor(type) { return (ACTIVITY_META[type] || { color: 'var(--color-border-strong)' }).color }

// 첨부 다운로드 URL
export function taskAttachmentUrl(taskId, att, inline = false) {
  const base = `/api/tasks/${taskId}/attachments/${att.id}/download`
  return inline ? `${base}?inline=1` : base
}

export function commentAttachmentUrl(taskId, commentId, att, inline = false) {
  const base = `/api/tasks/${taskId}/comments/${commentId}/attachments/${att.id}/download`
  return inline ? `${base}?inline=1` : base
}

export function humanSize(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
